/**
 * Timebase: the authoritative position model for the timeline.
 *
 * Two explicit timebases, both integer:
 *   - audio positions are SAMPLES (BigInt)
 *   - musical positions are TICKS  (BigInt)
 *
 * Tempo is stored as microseconds-per-quarter-note (the MIDI convention) so
 * every conversion is exact integer arithmetic rather than accumulated float
 * error. A tempo map holds one entry per tempo change with its precomputed
 * start sample, so conversions stay O(log n) and never drift.
 */

export const DEFAULT_PPQ = 960; // 10x the old 96 PPQ; integer ticks throughout
export const DEFAULT_SAMPLE_RATE = 48000;
export const DEFAULT_BPM = 120;

const MICROSECONDS_PER_MINUTE = 60_000_000;

export const bpmToUsPerQuarter = (bpm) => {
  const safeBpm = Number.isFinite(Number(bpm)) && Number(bpm) > 0 ? Number(bpm) : DEFAULT_BPM;
  return Math.max(1, Math.round(MICROSECONDS_PER_MINUTE / safeBpm));
};

export const usPerQuarterToBpm = (us) => MICROSECONDS_PER_MINUTE / Math.max(1, Number(us));

/** Round-half-up BigInt division (native BigInt division truncates). */
const divRound = (num, den) => {
  if (den === 0n) return 0n;
  const neg = (num < 0n) !== (den < 0n);
  const a = num < 0n ? -num : num;
  const b = den < 0n ? -den : den;
  const q = (a + (b / 2n)) / b;
  return neg ? -q : q;
};

const toBigInt = (value) => {
  if (typeof value === 'bigint') return value;
  const num = Number(value);
  if (!Number.isFinite(num)) return 0n;
  return BigInt(Math.round(num));
};

/** Samples spanned by `ticks` at a constant tempo. Exact integer math. */
function ticksToSamplesAt(ticks, usPerQuarter, ppq, sampleRate) {
  // samples = ticks * usPerQuarter * sampleRate / (ppq * 1e6)
  const num = toBigInt(ticks) * BigInt(usPerQuarter) * BigInt(sampleRate);
  const den = BigInt(ppq) * 1_000_000n;
  return divRound(num, den);
}

/** Inverse of ticksToSamplesAt. */
function samplesToTicksAt(samples, usPerQuarter, ppq, sampleRate) {
  const num = toBigInt(samples) * BigInt(ppq) * 1_000_000n;
  const den = BigInt(usPerQuarter) * BigInt(sampleRate);
  return divRound(num, den);
}

/**
 * Build a tempo map. `events` are tempo changes at tick positions; the first
 * is forced to tick 0 so every position resolves.
 */
export function createTempoMap({
  sampleRate = DEFAULT_SAMPLE_RATE,
  ppq = DEFAULT_PPQ,
  events = [{ tick: 0, bpm: DEFAULT_BPM }]
} = {}) {
  const normalized = (Array.isArray(events) && events.length ? events : [{ tick: 0, bpm: DEFAULT_BPM }])
    .map((e) => ({
      tick: toBigInt(e?.tick ?? 0),
      usPerQuarter: e?.usPerQuarter
        ? Math.max(1, Math.round(Number(e.usPerQuarter)))
        : bpmToUsPerQuarter(e?.bpm)
    }))
    .sort((a, b) => (a.tick < b.tick ? -1 : a.tick > b.tick ? 1 : 0));

  normalized[0].tick = 0n;

  // Collapse duplicate tick positions (last write wins).
  const segments = [];
  normalized.forEach((entry) => {
    const last = segments[segments.length - 1];
    if (last && last.tick === entry.tick) segments[segments.length - 1] = entry;
    else segments.push(entry);
  });

  // Precompute each segment's start sample by walking the map once.
  let runningSample = 0n;
  segments.forEach((segment, i) => {
    segment.startSample = runningSample;
    const next = segments[i + 1];
    if (next) {
      runningSample += ticksToSamplesAt(
        next.tick - segment.tick, segment.usPerQuarter, ppq, sampleRate
      );
    }
  });

  return { sampleRate, ppq, segments };
}

const segmentForTick = (map, tick) => {
  let lo = 0;
  let hi = map.segments.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (map.segments[mid].tick <= tick) lo = mid;
    else hi = mid - 1;
  }
  return map.segments[lo];
};

const segmentForSample = (map, sample) => {
  let lo = 0;
  let hi = map.segments.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (map.segments[mid].startSample <= sample) lo = mid;
    else hi = mid - 1;
  }
  return map.segments[lo];
};

/** Musical tick → audio sample. */
export function tickToSample(tick, map) {
  const t = toBigInt(tick);
  const seg = segmentForTick(map, t);
  return seg.startSample + ticksToSamplesAt(
    t - seg.tick, seg.usPerQuarter, map.ppq, map.sampleRate
  );
}

/** Audio sample → musical tick. */
export function sampleToTick(sample, map) {
  const s = toBigInt(sample);
  const seg = segmentForSample(map, s);
  return seg.tick + samplesToTicksAt(
    s - seg.startSample, seg.usPerQuarter, map.ppq, map.sampleRate
  );
}

/**
 * Audio sample → beat WITHOUT tick quantization.
 *
 * sampleToTick rounds to whole ticks (25 samples at 120bpm/48kHz), which is
 * correct for musical positions but destroys sample precision for audio. This
 * walks the same tempo segments in floating point instead, so derived beat
 * values stay faithful to the sample they came from.
 */
export function sampleToBeatExact(sample, map) {
  const s = toBigInt(sample);
  const seg = segmentForSample(map, s);
  const relativeSamples = Number(s - seg.startSample);
  const samplesPerQuarter = (seg.usPerQuarter * map.sampleRate) / 1_000_000;
  if (!(samplesPerQuarter > 0)) return Number(seg.tick) / map.ppq;
  return (Number(seg.tick) / map.ppq) + (relativeSamples / samplesPerQuarter);
}

/** Beat → sample without routing through integer ticks. */
export function beatToSampleExact(beat, map) {
  const safeBeat = Math.max(0, Number(beat) || 0);
  const targetTick = safeBeat * map.ppq;
  const seg = segmentForTick(map, toBigInt(Math.floor(targetTick)));
  const relativeQuarters = (targetTick - Number(seg.tick)) / map.ppq;
  const samplesPerQuarter = (seg.usPerQuarter * map.sampleRate) / 1_000_000;
  return seg.startSample + toBigInt(Math.round(relativeQuarters * samplesPerQuarter));
}

// ── Convenience helpers ────────────────────────────────────────────────────

export const beatsToTicks = (beats, map) => toBigInt(Math.round(Number(beats) * map.ppq));
export const ticksToBeats = (tick, map) => Number(toBigInt(tick)) / map.ppq;
export const beatToSample = (beat, map) => tickToSample(beatsToTicks(beat, map), map);
export const sampleToBeat = (sample, map) => ticksToBeats(sampleToTick(sample, map), map);
export const secondsToSamples = (seconds, map) => toBigInt(Number(seconds) * map.sampleRate);
export const samplesToSeconds = (sample, map) => Number(toBigInt(sample)) / map.sampleRate;

/** Ticks per bar for a given meter (4/4 by default). */
export const ticksPerBar = (map, beatsPerBar = 4) => BigInt(map.ppq) * BigInt(beatsPerBar);

export { toBigInt, divRound };
